import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MinusCircle, PlusCircle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

/** Rename a preset and edit its panel content using the current model selection. */
export function PresetEditDialog({ projectId, preset, selection = [], onOpenChange, onSaved }) {
  const [name, setName] = useState("");
  const [uids, setUids] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setName(preset?.name || "");
    setUids(preset?.panel_uids || []);
  }, [preset]);

  if (!preset) return null;

  const conditions = Object.entries(preset.filters || {}).filter(([, v]) => v);
  const newlySelected = selection.filter((uid) => !uids.includes(uid));
  const removable = selection.filter((uid) => uids.includes(uid));

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/projects/${projectId}/presets/${preset.id}`, {
        name: name.trim(),
        panel_uids: conditions.length && !uids.length ? null : uids,
      });
      toast.success(`Preset "${data.name}" updated`);
      onSaved?.();
      onOpenChange?.(false);
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={!!preset} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="preset-edit-dialog"
        className="border-slate-200 bg-white text-slate-900"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-lg">Edit preset</DialogTitle>
        </DialogHeader>

        <div className="space-y-1.5">
          <p className="mono text-[10px] uppercase tracking-[0.18em] text-slate-500">Name</p>
          <input
            data-testid="preset-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-lg border border-slate-200 bg-[#EEF2F8] px-3 py-2 text-sm text-slate-800"
          />
        </div>

        {conditions.length ? (
          <div className="rounded-lg border border-slate-200 bg-[#F8FAFC] p-3">
            <p className="mono text-[10px] uppercase tracking-[0.18em] text-slate-500">Filters</p>
            <p className="mono mt-1 text-[11px] text-slate-600">
              {conditions.map(([key, value]) => `${key}: ${value}`).join(" · ")}
            </p>
          </div>
        ) : null}

        <div className="rounded-lg border border-slate-200 bg-[#F8FAFC] p-3">
          <p className="mono text-[10px] uppercase tracking-[0.18em] text-slate-500">Panels</p>
          <p data-testid="preset-panel-count" className="mono mt-1 text-xs text-slate-700">
            {uids.length ? `${uids.length} panels stored` : "no stored panels (filter only)"}
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              data-testid="preset-add-selected"
              variant="outline"
              disabled={!newlySelected.length}
              onClick={() => setUids((prev) => [...prev, ...newlySelected])}
              className="rounded-full border-slate-200 text-[11px]"
            >
              <PlusCircle size={12} className="mr-1.5" /> Add selected ({newlySelected.length})
            </Button>
            <Button
              data-testid="preset-remove-selected"
              variant="outline"
              disabled={!removable.length}
              onClick={() =>
                setUids((prev) => prev.filter((uid) => !selection.includes(uid)))
              }
              className="rounded-full border-slate-200 text-[11px]"
            >
              <MinusCircle size={12} className="mr-1.5" /> Remove selected ({removable.length})
            </Button>
            {uids.length ? (
              <Button
                data-testid="preset-replace-selection"
                variant="outline"
                disabled={!selection.length}
                onClick={() => setUids(selection)}
                className="rounded-full border-slate-200 text-[11px]"
              >
                Replace with selection ({selection.length})
              </Button>
            ) : null}
          </div>
          {selection.length ? null : (
            <p className="mt-2 text-[10px] text-slate-500">
              Select panels in the model to add or remove them here.
            </p>
          )}
        </div>

        <Button
          data-testid="preset-save-button"
          disabled={busy || !name.trim() || (!uids.length && !conditions.length)}
          onClick={save}
          className="rounded-full bg-cyan-500 text-[#04121a] hover:bg-cyan-400"
        >
          Save preset
        </Button>
      </DialogContent>
    </Dialog>
  );
}
